import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Chip
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import InfoIcon from '@mui/icons-material/Info';
import { validateWithExternalService } from '../utils/externalValidationService';
import ValidationResults from './ValidationResults';

const ExternalValidation = ({ ediContent }) => {
  const [apiKey, setApiKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [error, setError] = useState('');
  const serviceId = 'norwegianEdiValidator';
  const serviceName = 'Norwegian EDI Validator';

  // Load saved API key on component mount
  useEffect(() => {
    const savedKey = localStorage.getItem(`edi_validation_key_${serviceId}`);
    if (savedKey) {
      setApiKey(savedKey);
    }
  }, []);
  
  const handleApiKeyChange = (e) => {
    setApiKey(e.target.value);
  };

  const runExternalValidation = async () => {
    setIsValidating(true);
    setError('');
    setValidationResults(null);
    
    try {
      // Save API key for convenience
      if (apiKey) {
        localStorage.setItem(`edi_validation_key_${serviceId}`, apiKey);
      }
      
      const results = await validateWithExternalService(
        serviceId,
        ediContent,
        apiKey
      );
      
      setValidationResults(results);
    } catch (err) {
      console.error('Validation error:', err);
      setError(err.message);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body1" fontWeight="medium">
          Demo Feature
        </Typography>
        <Typography variant="body2">
          This validation service is for demonstration purposes only and is not connected to real APIs. 
          The results shown are simulated to demonstrate how external validation would work in a production environment.
        </Typography>
      </Alert>
      
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          External Validation Services
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Validate your EANCOM/EDIFACT file using third-party validation services. An API key is required.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mr: 1 }}>
              {serviceName}
            </Typography>
            <Chip 
              label="Demo" 
              size="small" 
              color="info" 
              variant="outlined" 
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
          
          <TextField
            label="API Key"
            variant="outlined"
            value={apiKey}
            onChange={handleApiKeyChange}
            sx={{ flexGrow: 1 }}
            type="password"
            disabled={isValidating}
            placeholder="Enter your API key"
          />
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudSyncIcon />}
            onClick={runExternalValidation}
            disabled={isValidating || !apiKey}
            sx={{ height: 56, px: 3, whiteSpace: 'nowrap' }}
          >
            {isValidating ? <CircularProgress size={24} color="inherit" /> : 'Validate with Service'}
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Supported Standards:</strong> EDIFACT, EANCOM, STAND
          </Typography>
        </Box>
      </Paper>
      
      {isValidating && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4, flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress size={40} color="primary" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Validating with {serviceName}...
          </Typography>
        </Box>
      )}
      
      {validationResults && !isValidating && (
        <Box sx={{ mt: 4 }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Results from {serviceName}
            </Typography>
            
            {validationResults.rawResults?.demo && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This is a simulated validation result for demonstration purposes.
              </Alert>
            )}
            
            <ValidationResults 
              validationResults={validationResults}
              isValidating={false}
              messageType={validationResults.messageType}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ExternalValidation; 